import React, { useState } from 'react';
import {
  Button,
  Highlight,
  HStack,
  IconBannersWarning,
  ScrollView,
  Text,
  VStack,
} from '@adc/parallax-component-library';
import * as Yup from 'yup';
import { Dialog } from 'tamagui';
import i18n from '../../utilities/i18n';
import './Modal.scss';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import TextInputWithError from '../ui/forms/TextInputWithError';
import {
  Account,
  AccountCreationRequest,
  AccountCreationResult,
} from '../../models';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  accountsQueryOptions,
  createAccountMutateOptions,
} from '../../queryOptions';
import PasswordInputWithError from '../ui/forms/PasswordInputWithError';
import { useAppSelector } from '../../reducers/hooks';
import { generateYupPasswordSchema } from '../../services/auth';
import { getAuthenticationError } from '../../utilities/auth';

type Props = {
  trigger: React.ReactNode;
};

/**
 * Modal for creating a new user account. The registration API handles email
 * verification when it is required by environment configuration.
 * @param {React.ReactNode} trigger - The element that triggers the modal.
 * @returns {React.JSX.Element}
 */
export function CreateAccountModal({ trigger }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const queryClient = useQueryClient();

  const { passwordRules } = useAppSelector((state) => state.config);

  const createAccount = useMutation(
    createAccountMutateOptions({
      onSuccess: (result: AccountCreationResult) =>
        queryClient.setQueryData(
          accountsQueryOptions().queryKey,
          (accounts: Account[] | undefined) => [
            ...(accounts ?? []),
            result.account,
          ],
        ),
    }),
  );

  const initialValues: AccountCreationRequest = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  };

  const validationSchema = Yup.object()
    .shape({
      firstName: Yup.string().required(i18n.t('Form.genericRequired')),
      lastName: Yup.string().required(i18n.t('Form.genericRequired')),
      email: Yup.string()
        .required(i18n.t('Form.genericRequired'))
        .email(i18n.t('Form.emailInvalid')),
      password: Yup.string()
        .required(i18n.t('Form.genericRequired'))
        .min(8, i18n.t('Form.passwordMinLength')),
      confirmPassword: Yup.string()
        .required(i18n.t('Form.genericRequired'))
        .oneOf([Yup.ref('password')], i18n.t('Form.passwordsDoNotMatch')),
    })
    .concat(generateYupPasswordSchema(passwordRules));

  const onClose = (helpers: FormikHelpers<AccountCreationRequest>) => {
    helpers.resetForm();
    setIsOpen(false);
    setErrorMessage('');
    setAccountCreated(false);
  };

  const onSubmit = async (
    values: AccountCreationRequest,
    helpers: FormikHelpers<AccountCreationRequest>,
  ) => {
    await createAccount
      .mutateAsync({
        account: values,
      })
      .then((result) => {
        if (result.verificationRequired && !result.verificationEmailSent) {
          setErrorMessage(i18n.t('Modal_CreateAccount.error_user_invite'));
          setAccountCreated(true);
        } else {
          onClose(helpers);
        }
      })
      .catch((err) => {
        if (err.status === 409) {
          setErrorMessage(i18n.t('Modal_CreateAccount.error_duplicate_email'));
        } else if (err.status === 400) {
          setErrorMessage(
            getAuthenticationError(
              err.data.data, // TODO: Remove nested data.data after CSP-2256
              'Modal_CreateAccount.error_generic',
            ),
          );
        } else {
          setErrorMessage(i18n.t('Modal_CreateAccount.error_generic'));
        }
      });
  };

  return (
    <Dialog open={isOpen}>
      <Dialog.Trigger asChild onPress={() => setIsOpen(true)}>
        {trigger}
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content" width="50%">
          <HStack justifyContent="space-between">
            <Dialog.Title>
              <Text type="$h3.strong">
                {i18n.t('Modal_CreateAccount.title')}
              </Text>
            </Dialog.Title>
          </HStack>

          {errorMessage ? (
            <VStack paddingVertical={'$4'}>
              <Highlight
                variant="danger"
                iconStart={
                  <IconBannersWarning
                    color="$neutral.0"
                    accentColor="$support.danger.default"
                  />
                }
                title={i18n.t('Modal_CreateAccount.error_title')}
                body={i18n.t(errorMessage)}
                testID="error-banner"
              />
            </VStack>
          ) : null}

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {(formikProps: FormikProps<AccountCreationRequest>) => (
              <Form
                id="create-account-form"
                noValidate
                className="modal-form-layout"
              >
                <ScrollView flex={1} minHeight={0} marginVertical="$4">
                  <VStack gap="$2">
                    <HStack gap="$4">
                      <VStack flex={1}>
                        <TextInputWithError
                          label={i18n.t('Account.firstName')}
                          name="firstName"
                          testID="first-name"
                        />
                      </VStack>
                      <VStack flex={1}>
                        <TextInputWithError
                          label={i18n.t('Account.lastName')}
                          name="lastName"
                          testID="last-name"
                        />
                      </VStack>
                    </HStack>

                    <TextInputWithError
                      label={i18n.t('Account.email')}
                      name="email"
                      testID="email"
                    />

                    <HStack gap="$4">
                      <VStack flex={1}>
                        <PasswordInputWithError
                          name="password"
                          label={i18n.t('Account.password')}
                          testID="password"
                          autocomplete="new-password"
                        />
                      </VStack>
                      <VStack flex={1}>
                        <PasswordInputWithError
                          name="confirmPassword"
                          label={i18n.t('Account.confirmPassword')}
                          testID="confirm-password"
                          autocomplete="new-password"
                        />
                      </VStack>
                    </HStack>

                    <VStack alignSelf="flex-start">
                      <Text type={'$labelBase.default'} textAlign="left">
                        {i18n.t('PasswordRequirements.passwordRequirements')}
                      </Text>
                      {passwordRules.requirements.map((req) => (
                        <Text
                          type={'$labelBase.default'}
                          key={req}
                          marginLeft={'$2'}
                          textAlign="left"
                        >
                          {`\u2022 ${i18n.t(`PasswordRequirements.${req}`, {
                            min: passwordRules.rules.min?.value,
                            max: passwordRules.rules.max?.value,
                            count: passwordRules.rules.count?.value || 1,
                          })}`}
                        </Text>
                      ))}
                    </VStack>
                  </VStack>
                </ScrollView>

                <HStack justifyContent="flex-end" gap="$2" flexShrink={0}>
                  {accountCreated ? (
                    <Button
                      variant="secondary"
                      label={i18n.t('Common.close')}
                      aria-label={i18n.t('Common.close')}
                      testID="close"
                      onPress={() => onClose(formikProps)}
                    />
                  ) : (
                    <>
                      <Button
                        variant="secondary"
                        label={i18n.t('Common.cancel')}
                        aria-label={i18n.t('Common.cancel')}
                        testID="cancel"
                        disabled={formikProps.isSubmitting}
                        onPress={() => onClose(formikProps)}
                      />
                      <Button
                        variant="primary"
                        onPress={formikProps.submitForm}
                        label={i18n.t('Common.save')}
                        testID="save-account"
                        disabled={
                          formikProps.isSubmitting || !formikProps.isValid
                        }
                      />
                    </>
                  )}
                </HStack>
              </Form>
            )}
          </Formik>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
